import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

export default function Login() {
  const [authMethod, setAuthMethod] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(new Date());

  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const API_BASE_URL = 'https://api.avtechbmt.cloud/api/v1/auth';

  /* Live clock for the editorial panel */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');

      loginWithToken(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');

      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp,
          name: name.trim() || email.split('@')[0]
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Invalid OTP');

      loginWithToken(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const decodedToken = JSON.parse(atob(credentialResponse.credential.split('.')[1]));

      const res = await fetch(`${API_BASE_URL}/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          googleId: decodedToken.sub,
          name: decodedToken.name,
          email: decodedToken.email
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Google sign-in failed');

      loginWithToken(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - AV Tech Baramati</title>
        <meta name="description" content="Login to your AV Tech account terminal using Password, OTP, or Google." />
        <link rel="canonical" href="https://avtechbmt.cloud/login" />
      </Helmet>

      <div className="av-auth-page">
        <div className="av-auth-grain" aria-hidden="true" />

        {/* ============ LEFT — AUTH CARD ============ */}
        <main className="av-auth-left">
          <div className="av-auth-card">
            <span className="av-auth-tag">01 / Security</span>
            <h2 className="av-auth-title">
              Access <em>Terminal</em>
            </h2>
            <p className="av-auth-subtitle">
              Sign in with your credentials to continue.
            </p>

            <div className="av-auth-method-toggle">
              <button
                type="button"
                className={`av-auth-method-btn ${
                  authMethod === 'password' ? 'av-auth-method-btn--active' : ''
                }`}
                onClick={() => {
                  setAuthMethod('password');
                  setError('');
                  setStep(1);
                }}
              >
                Password
              </button>
              <button
                type="button"
                className={`av-auth-method-btn ${
                  authMethod === 'otp' ? 'av-auth-method-btn--active' : ''
                }`}
                onClick={() => {
                  setAuthMethod('otp');
                  setError('');
                  setStep(1);
                }}
              >
                OTP Code
              </button>
            </div>

            {error && <div className="av-auth-error">{error}</div>}

            {authMethod === 'password' ? (
              <form onSubmit={handlePasswordLogin} className="av-auth-form">
                <div className="av-auth-form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="engineer@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="av-auth-form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <button type="submit" disabled={loading} className="av-auth-submit">
                  {loading ? 'Authenticating…' : 'Sign In →'}
                </button>
              </form>
            ) : step === 1 ? (
              <form onSubmit={handleSendOtp} className="av-auth-form">
                <div className="av-auth-form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="engineer@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <button type="submit" disabled={loading} className="av-auth-submit">
                  {loading ? 'Sending OTP…' : 'Send Security Code →'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="av-auth-form">
                <div className="av-auth-form-group">
                  <label>Full Name (Optional for new users)</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="av-auth-form-group">
                  <label>Enter 6-Digit OTP</label>
                  <input
                    type="text"
                    required
                    maxLength="6"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <button type="submit" disabled={loading} className="av-auth-submit">
                  {loading ? 'Verifying…' : 'Verify & Access →'}
                </button>
                <button
                  type="button"
                  className="av-auth-resend"
                  onClick={() => setStep(1)}
                >
                  ← Change Email / Resend
                </button>
              </form>
            )}

            <div className="av-auth-divider">
              <span>OR</span>
            </div>

            <div className="av-auth-google-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google Authentication Failed')}
                text="signin_with"
                shape="rectangular"
                theme="outline"
              />
            </div>

            <p className="av-auth-footer">
              Don't have an account? <Link to="/register">Register here</Link>
            </p>
          </div>
        </main>

        {/* ============ RIGHT — EDITORIAL PANEL ============ */}
        <aside className="av-auth-right">
          <div className="av-auth-giant-type" aria-hidden="true">
            <span>ACCESS</span>
          </div>

          <div className="av-auth-right-top">
            <div className="av-auth-right-badge">
              <span className="av-auth-right-badge-dot" />
              <span className="av-auth-tag">01 / Security</span>
            </div>

            <h1 className="av-auth-right-title">
              Welcome back to the
              <br />
              <em>Access Terminal.</em>
            </h1>

            <p className="av-auth-right-sub">
              Sign in to your AV Tech account to manage projects, monitor deployments,
              and access your workspace. Choose password, OTP, or Google to continue.
            </p>

            <ul className="av-auth-right-features">
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Enterprise-grade encrypted sessions</span>
              </li>
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Multi-factor OTP & Google SSO support</span>
              </li>
              <li className="av-auth-right-feature">
                <span className="av-auth-right-feature-check">✓</span>
                <span>Live workspace access from any device</span>
              </li>
            </ul>
          </div>

          <div className="av-auth-right-bottom">
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Local Time</span>
              <span className="av-auth-right-meta-value">
                {clock.toLocaleTimeString('en-US', { hour12: false })}
              </span>
            </div>
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Status</span>
              <span className="av-auth-right-meta-value av-auth-right-meta-value--live">
                <span className="av-auth-live-dot" /> Secure
              </span>
            </div>
            <div className="av-auth-right-meta-item">
              <span className="av-auth-right-meta-label">Location</span>
              <span className="av-auth-right-meta-value">Baramati, IN</span>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}